import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';

import Modal from '../Modal/Modal';
import Button, { SecondaryButton } from '../Button/Button';
import IconAlert from '../IconAlert/IconAlert';

import css from './DeleteListingModal.module.css';

/**
 * Delete listing confirmation modal.
 *
 * Prompts the listing owner to confirm delisting/deleting their listing from the marketplace.
 *
 * @param {Object} props
 * @param {string} [props.id] - Modal identifier
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {string} [props.listingTitle] - Listing title displayed in the confirmation
 * @param {function} props.onCloseModal - Callback when modal is dismissed or cancelled
 * @param {function} props.onDeleteListing - Callback when user confirms listing deletion
 * @param {boolean} [props.inProgress] - Loading indicator state
 * @param {function} [props.onManageDisableScrolling] - Optional scroll lock callback
 * @param {string} [props.className] - Optional custom CSS class
 * @param {string} [props.rootClassName] - Optional root CSS class
 * @returns {JSX.Element} Delete listing modal component
 */
const noop = () => {};

const DeleteListingModal = props => {
  const intl = useIntl();
  const {
    id = 'DeleteListingModal',
    className,
    rootClassName,
    isOpen,
    listingTitle,
    focusElementId,
    onCloseModal,
    onManageDisableScrolling = noop,
    onDeleteListing,
    inProgress = false,
  } = props;

  const classes = classNames(rootClassName || css.root, className);

  return (
    <Modal
      id={id}
      containerClassName={classes}
      contentClassName={css.modalContent}
      isOpen={isOpen}
      onClose={onCloseModal}
      onManageDisableScrolling={onManageDisableScrolling}
      focusElementId={focusElementId}
      usePortal
      closeButtonMessage={intl.formatMessage({ id: 'DeleteListingModal.close' })}
    >
      <IconAlert className={css.modalIcon} />
      <p className={css.modalTitle}>
        <FormattedMessage
          id="DeleteListingModal.title"
          values={{ listingTitle: listingTitle || '' }}
        />
      </p>
      <p className={css.modalMessage}>
        <FormattedMessage
          id="DeleteListingModal.message"
          values={{ listingTitle: listingTitle || '' }}
        />
      </p>
      <div className={css.buttonGroup}>
        <SecondaryButton
          onClick={onCloseModal}
          disabled={inProgress}
          className={css.cancelButton}
        >
          <FormattedMessage id="DeleteListingModal.cancel" />
        </SecondaryButton>
        <Button
          onClick={onDeleteListing}
          inProgress={inProgress}
          disabled={inProgress}
          className={css.deleteButton}
        >
          <FormattedMessage id="DeleteListingModal.submit" />
        </Button>
      </div>
    </Modal>
  );
};

DeleteListingModal.propTypes = {
  id: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  listingTitle: PropTypes.string,
  onCloseModal: PropTypes.func.isRequired,
  onDeleteListing: PropTypes.func.isRequired,
  inProgress: PropTypes.bool,
  onManageDisableScrolling: PropTypes.func,
  focusElementId: PropTypes.string,
  className: PropTypes.string,
  rootClassName: PropTypes.string,
};

export default DeleteListingModal;
